import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sprout, X } from "lucide-react";
import { toast } from "sonner";

const DISMISS_KEY = "civic_launch_interest_dismissed";

// Phase 17-C item 9: launch-area interest capture. Shown only when launch
// areas are configured (backend-driven config) and the visitor is signed out.
// Stores EMAIL + OUTWARD CODE ONLY. If the entered district is already live,
// nothing is stored — we point them at collectives instead.
export function LaunchInterestBanner() {
  const { user } = useAuth();
  const [areas, setAreas] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });
  const [email, setEmail] = useState("");
  const [outcode, setOutcode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {live, area?} | {stored}

  useEffect(() => {
    api.launchConfig().then((r) => setAreas(r.launch_areas || [])).catch(() => setAreas([]));
  }, []);

  if (user || dismissed || areas.length === 0) return null;

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.launchInterest(email.trim(), outcode.trim());
      setResult(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Check the email and postcode district (e.g. SW9)");
    } finally { setBusy(false); }
  };

  return (
    <section className="bg-forest/[0.04] border-y border-forest/10" data-testid="launch-interest-banner">
      <div className="max-w-6xl mx-auto px-6 py-10 relative">
        <button
          data-testid="launch-interest-dismiss"
          onClick={dismiss}
          aria-label="Dismiss"
          className="absolute top-4 right-5 text-neutral-400 hover:text-forest transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex flex-wrap items-center gap-x-10 gap-y-5">
          <div className="max-w-md">
            <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold inline-flex items-center gap-1.5">
              <Sprout className="w-4 h-4" /> Growing street by street
            </div>
            <p className="text-sm text-neutral-600 mt-2">
              We're live in {areas.join(", ")} first. Somewhere else? Leave your email and postcode
              district and we'll tell you the moment we reach your streets.
            </p>
          </div>
          {result ? (
            result.live ? (
              <p data-testid="launch-interest-live" className="text-sm font-medium text-forest">
                Good news — we're already live in {result.area}!{" "}
                <Link to="/collectives" className="underline">Find your collective</Link>
              </p>
            ) : (
              <p data-testid="launch-interest-done" className="text-sm font-medium text-forest">
                You're on the list — we'll email you when we reach {outcode.trim().toUpperCase()}.
              </p>
            )
          ) : (
            <form onSubmit={submit} className="flex flex-wrap gap-2 items-center" data-testid="launch-interest-form">
              <Input
                data-testid="launch-interest-outcode"
                required
                value={outcode}
                onChange={(e) => setOutcode(e.target.value)}
                placeholder="District (SW9)"
                className="h-10 rounded-xl bg-white w-32"
              />
              <Input
                data-testid="launch-interest-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-10 rounded-xl bg-white w-56"
              />
              <Button type="submit" disabled={busy} data-testid="launch-interest-submit" className="rounded-full h-10 bg-forest hover:bg-forest-hover px-5">
                {busy ? "Checking…" : "Keep me posted"}
              </Button>
              <p className="w-full text-[11px] text-neutral-500">We store your email and the postcode district only.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
